// Full-document render of the tempurpedic FlagshipPage: PageSkeleton (the
// React document shell) wrapping the FlagshipPageContent body stream (normal
// site chrome). The body dispatches as ONE React tree in Fragment mode (no
// per-block `w-block-*` wrappers) through the shared Toolbox page map
// (FlagshipPageContent extends the Toolbox) plus the 12 flagship block
// entries below.
//
// Flagship side-channels (get_flagship_block_ssr, keyed by block.id):
//   - `location` (StaticBlock) -> the shared FlagshipInfoTemplate props;
//     rendered ONLY when the page has a linked retail location (a
//     side-channel miss renders null).
//   - `product_assortment` / `products` (DEPRECATED) -> the full presenter
//     envelopes (settings/request/value-derived, price-gated server-side).
//   - `store_detail` -> the block value PLUS the page-level store-hours
//     lead-in + franchisee-disclaimer props.
//   - `store_front` (StaticBlock) -> the whole envelope (storefront
//     `original` rendition URL + intro/title/district).
//   - `reviews` -> the block value PLUS the page-title lead-in. NOTE:
//     FlagshipPageContent re-binds the Toolbox `reviews` stream key to
//     FlagshipReviewsBlock, so this entry OVERRIDES the toolbox map's.
import { Immutable } from "immer";
import React from "react";

import { templates } from "@reactivated";
import type {
    FeaturedAssociateBlock as FeaturedAssociateBlockValue,
    FeaturedReviewBlock as FeaturedReviewBlockValue,
    FlagshipCarouselBlock as FlagshipCarouselBlockValue,
    FlagshipReviewsBlock as FlagshipReviewsBlockValue,
    MediaObjectBlock as MediaObjectBlockValue,
    SlideShowBlock as SlideShowBlockValue,
    SocialNetworkBlock as SocialNetworkBlockValue,
    StoreDetailBlock as StoreDetailBlockValue,
} from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { buildToolboxPageMap } from "../blocks/toolboxPageMap";
import { FlagshipCarouselBlock } from "../components/FlagshipCarouselBlock";
import { FlagshipFeaturedAssociateBlock } from "../components/FlagshipFeaturedAssociateBlock";
import { FlagshipFeaturedReviewBlock } from "../components/FlagshipFeaturedReviewBlock";
import { FlagshipMediaObjectBlock } from "../components/FlagshipMediaObjectBlock";
import { FlagshipReviewsBlock } from "../components/FlagshipReviewsBlock";
import { FlagshipSlideShowBlock } from "../components/FlagshipSlideShowBlock";
import { FlagshipSocialNetworkBlock } from "../components/FlagshipSocialNetworkBlock";
import { FlagshipStoreDetailBlock } from "../components/FlagshipStoreDetailBlock";
import { PageSkeleton } from "../components/PageSkeleton";
import { Template as FlagshipInfo } from "./FlagshipInfoTemplate";
import { Template as FlagshipProductAssortmentBlock } from "./FlagshipProductAssortmentBlockTemplate";
import { Template as FlagshipProductsBlock } from "./FlagshipProductsBlockTemplate";
import { Template as FlagshipStoreFrontBlock } from "./FlagshipStoreFrontBlockTemplate";

// Mutable reactivated props (not Immutable<>): this template composes the
// in-tree SiteHeader chain via PageSkeleton, which is typed with mutable
// reactivated props (see PageSkeleton).
type IProps = templates.FlagshipPageTemplate;

const buildFlagshipPageMap = (props: IProps) => ({
    ...buildToolboxPageMap(props),

    // --- Pure value blocks (each dispatches its standalone Template) ---
    carousel: (block: { value: Immutable<FlagshipCarouselBlockValue> }) => (
        <FlagshipCarouselBlock value={block.value} />
    ),
    featured_associate: (block: {
        value: Immutable<FeaturedAssociateBlockValue>;
    }) => <FlagshipFeaturedAssociateBlock value={block.value} />,
    featured_review: (block: {
        value: Immutable<FeaturedReviewBlockValue>;
    }) => <FlagshipFeaturedReviewBlock value={block.value} />,
    media_object: (block: { value: Immutable<MediaObjectBlockValue> }) => (
        <FlagshipMediaObjectBlock value={block.value} />
    ),
    slide_show: (block: { value: Immutable<SlideShowBlockValue> }) => (
        <FlagshipSlideShowBlock value={block.value} />
    ),
    social_links: (block: { value: Immutable<SocialNetworkBlockValue> }) => (
        <FlagshipSocialNetworkBlock value={block.value} />
    ),

    // --- Side-channel blocks (props looked up by block.id) ---
    location: (block: { id?: string }) => {
        const fi = block.id ? props.location_ssr[block.id] : undefined;
        // No linked retail location -> render nothing.
        return fi ? <FlagshipInfo {...fi} /> : null;
    },
    product_assortment: (block: { id?: string }) => {
        const pa = block.id
            ? props.product_assortment_ssr[block.id]
            : undefined;
        return pa ? <FlagshipProductAssortmentBlock {...pa} /> : null;
    },
    products: (block: { id?: string }) => {
        const pb = block.id ? props.products_ssr[block.id] : undefined;
        return pb ? <FlagshipProductsBlock {...pb} /> : null;
    },
    store_front: (block: { id?: string }) => {
        const sf = block.id ? props.store_front_ssr[block.id] : undefined;
        return sf ? <FlagshipStoreFrontBlock {...sf} /> : null;
    },
    store_detail: (block: {
        id?: string;
        value: Immutable<StoreDetailBlockValue>;
    }) => {
        const sd = block.id ? props.store_detail_ssr[block.id] : undefined;
        return (
            <FlagshipStoreDetailBlock
                value={block.value}
                page_title={sd?.page_title ?? ""}
                hours_summary={sd?.hours_summary ?? ""}
                upcoming_holiday_hours_summary={
                    sd?.upcoming_holiday_hours_summary ?? []
                }
                show_franchisee_disclaimer={
                    sd?.show_franchisee_disclaimer ?? false
                }
            />
        );
    },
    reviews: (block: {
        id?: string;
        value: Immutable<FlagshipReviewsBlockValue>;
    }) => (
        <FlagshipReviewsBlock
            value={block.value}
            page_title={
                block.id ? (props.flagship_reviews_ssr[block.id] ?? "") : ""
            }
        />
    ),
});

export const Template = (props: IProps): React.JSX.Element => {
    const body = props.page.body;
    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            {body && (
                <StreamField
                    tagName={React.Fragment}
                    value={body.value}
                    components={buildFlagshipPageMap(props)}
                />
            )}
        </PageSkeleton>
    );
};
